/**
 * World 配置块 JSON Schema 定义
 */
export const worldSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/world.json',
  title: 'World 配置',
  description: 'World（世界）配置数据结构',
  type: 'object',
  required: ['world_id', 'name'],
  properties: {
    world_id: {
      type: 'string',
      title: 'World ID',
      description: 'World 唯一标识符',
      pattern: '^world_[a-zA-Z0-9_]+$',
      minLength: 7,
      maxLength: 50,
      examples: ['world_main', 'world_51_station', 'world_mystery_city'],
    },
    name: {
      type: 'string',
      title: '世界名称',
      description: '世界的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['51号兵站', '神秘都市', '赛博朋克2077'],
    },
    description: {
      type: 'string',
      title: '世界描述',
      description: '世界的详细描述',
      minLength: 0,
      maxLength: 2000,
    },
    setting: {
      type: 'string',
      title: '世界设定',
      description: '世界的背景设定',
      enum: ['现代', '未来', '历史', '奇幻', '科幻', '赛博朋克', '蒸汽朋克', '其他'],
    },
    atmosphere: {
      type: 'string',
      title: '氛围',
      description: '世界的整体氛围',
      enum: ['神秘', '紧张', '轻松', '恐怖', '悬疑', '冒险', '浪漫', '其他'],
    },
    time_period: {
      type: 'string',
      title: '时代背景',
      description: '世界所处的时代',
      maxLength: 100,
      examples: ['1940年代', '2077年', '中世纪', '近未来'],
    },
    geography: {
      type: 'object',
      title: '地理设定',
      description: '世界的地理信息',
      properties: {
        locations: {
          type: 'array',
          title: '地点列表',
          items: {
            type: 'string',
            pattern: '^location_[a-zA-Z0-9_]+$',
          },
        },
        map_data: {
          type: 'object',
          title: '地图数据',
          description: '地图的结构化数据',
          additionalProperties: true,
        },
      },
    },
    rules: {
      type: 'array',
      title: '世界规则',
      description: '世界中的特殊规则',
      items: {
        type: 'object',
        required: ['rule_id', 'description'],
        properties: {
          rule_id: {
            type: 'string',
            title: '规则 ID',
            pattern: '^rule_[a-zA-Z0-9_]+$',
          },
          name: {
            type: 'string',
            title: '规则名称',
            maxLength: 100,
          },
          description: {
            type: 'string',
            title: '规则描述',
            maxLength: 500,
          },
          effect: {
            type: 'string',
            title: '规则效果',
            description: '规则生效的逻辑表达式',
          },
        },
      },
    },
    factions: {
      type: 'array',
      title: '派系列表',
      description: '世界中存在的派系',
      items: {
        type: 'string',
        pattern: '^faction_[a-zA-Z0-9_]+$',
      },
    },
    player_template: {
      type: 'object',
      title: '玩家模板',
      description: '玩家初始属性模板',
      properties: {
        health: {
          type: 'integer',
          title: '初始生命值',
          minimum: 1,
          default: 100,
        },
        morale: {
          type: 'integer',
          title: '初始士气',
          minimum: 0,
          maximum: 100,
          default: 100,
        },
        inventory_size: {
          type: 'integer',
          title: '背包容量',
          minimum: 1,
          default: 20,
        },
        initial_items: {
          type: 'array',
          title: '初始物品',
          items: {
            type: 'string',
            pattern: '^item_[a-zA-Z0-9_]+$',
          },
        },
        attributes: {
          type: 'object',
          title: '初始属性',
          additionalProperties: {
            type: 'integer',
            minimum: 0,
          },
        },
      },
    },
    game_modes: {
      type: 'array',
      title: '游戏模式',
      description: '支持的游戏模式',
      items: {
        type: 'object',
        properties: {
          mode_id: {
            type: 'string',
            title: '模式 ID',
          },
          mode_name: {
            type: 'string',
            title: '模式名称',
          },
          description: {
            type: 'string',
            title: '模式描述',
          },
          min_players: {
            type: 'integer',
            title: '最少玩家数',
            minimum: 1,
          },
          max_players: {
            type: 'integer',
            title: '最多玩家数',
            minimum: 1,
          },
        },
      },
    },
    tags: {
      type: 'array',
      title: '标签',
      description: '世界的分类标签',
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 20,
      },
      uniqueItems: true,
      examples: [['悬疑', '推理', '多人'], ['科幻', '射击', '合作']],
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type WorldConfig = {
  world_id: string
  name: string
  description?: string
  setting?: string
  atmosphere?: string
  time_period?: string
  geography?: {
    locations?: string[]
    map_data?: Record<string, any>
  }
  rules?: Array<{
    rule_id: string
    name?: string
    description: string
    effect?: string
  }>
  factions?: string[]
  player_template?: {
    health?: number
    morale?: number
    inventory_size?: number
    initial_items?: string[]
    attributes?: Record<string, number>
  }
  game_modes?: Array<{
    mode_id?: string
    mode_name?: string
    description?: string
    min_players?: number
    max_players?: number
  }>
  tags?: string[]
  metadata?: Record<string, any>
}
